/** A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. */

/**
 * Format type constants organized by category and precision.
 *
 * Categories: number, dollars, percent
 * Precision:  (base) = integer, Tenths = 1 decimal, Hundredths = 2 decimals
 * Special:    Abbr = compact notation, percentNum = raw number × 100
 */
export const FORMAT = {
  // Numbers (with commas)
  NUMBER: "number", // 1,234
  NUMBER_TENTHS: "numberTenths", // 1,234.5
  NUMBER_HUNDREDTHS: "numberHundredths", // 1,234.56
  NUMBER_ABBR: "numberAbbr", // 1.23K

  // Dollars
  DOLLARS: "dollars", // $1,234
  DOLLARS_HUNDREDTHS: "dollarsHundredths", // $1,234.56
  DOLLARS_ABBR: "dollarsAbbr", // $1.23M

  // Percents
  PERCENT: "percent", // 45%
  PERCENT_TENTHS: "percentTenths", // 45.1%
  PERCENT_HUNDREDTHS: "percentHundredths", // 45.12%
  PERCENT_NUM: "percentNum", // 45.1 (no % sign, multiplies by 100)
};

// Default text for missing values
const NA_LABEL = "Data is not available.";

/**
 * @typedef {typeof FORMAT[keyof typeof FORMAT]} Format
 */

// -- Intl.NumberFormat instances (reused across calls) --

// Numbers
const fmtNumber = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const fmtNumberTenths = new Intl.NumberFormat("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtNumberHundredths = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNumberAbbr = new Intl.NumberFormat("en-US", { notation: "compact", maximumSignificantDigits: 3 });

// Dollars
const fmtDollars = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtDollarsHundredths = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDollarsAbbr = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumSignificantDigits: 3 });

// Percents
const fmtPercent = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 0 });
const fmtPercentTenths = new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtPercentHundredths = new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPercentNum = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

// -- Formatter helper --

/**
 * @param {Intl.NumberFormat} fmt
 * @param {number} [multiplier]
 * @returns {(d: string | number | null, naLabel?: string) => string}
 */
function createFormatter(fmt, multiplier) {
  return (d, naLabel = NA_LABEL) => {
    if (d === null || d === undefined) return naLabel;
    if (typeof d === "string") return d;
    return fmt.format(multiplier ? d * multiplier : d);
  };
}

// -- Exported FORMATTERS object --

/**
 * @type {Record<Format, (d: string | number | null, naLabel?: string) => string>}
 */
export const FORMATTERS = {
  // Numbers
  [FORMAT.NUMBER]: createFormatter(fmtNumber),
  [FORMAT.NUMBER_TENTHS]: createFormatter(fmtNumberTenths),
  [FORMAT.NUMBER_HUNDREDTHS]: createFormatter(fmtNumberHundredths),
  [FORMAT.NUMBER_ABBR]: createFormatter(fmtNumberAbbr),

  // Dollars
  [FORMAT.DOLLARS]: createFormatter(fmtDollars),
  [FORMAT.DOLLARS_HUNDREDTHS]: createFormatter(fmtDollarsHundredths),
  [FORMAT.DOLLARS_ABBR]: createFormatter(fmtDollarsAbbr),

  // Percents
  [FORMAT.PERCENT]: createFormatter(fmtPercent),
  [FORMAT.PERCENT_TENTHS]: createFormatter(fmtPercentTenths),
  [FORMAT.PERCENT_HUNDREDTHS]: createFormatter(fmtPercentHundredths),
  [FORMAT.PERCENT_NUM]: createFormatter(fmtPercentNum, 100),
};

// -- Legend formatters (compact variants for tick labels) --

// Numbers → compact notation, 2 significant digits
const fmtLegendNumber = new Intl.NumberFormat("en-US", { notation: "compact", maximumSignificantDigits: 2 });

// Dollars → compact notation, 2 significant digits
const fmtLegendDollars = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumSignificantDigits: 2 });

// Percents → integer only
const fmtLegendPercent = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 0 });
const fmtLegendPercentNum = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/**
 * Legend-specific formatters: compact versions of each format type,
 * designed for short tick labels on legends and color scales.
 * Big numbers get abbreviated (1M, 2.5B), percents get rounded to integers.
 * @type {Record<Format, (d: string | number | null, naLabel?: string) => string>}
 */
export const LEGEND_FORMATTERS = {
  // Numbers → all use compact notation
  [FORMAT.NUMBER]: createFormatter(fmtLegendNumber),
  [FORMAT.NUMBER_TENTHS]: createFormatter(fmtLegendNumber),
  [FORMAT.NUMBER_HUNDREDTHS]: createFormatter(fmtLegendNumber),
  [FORMAT.NUMBER_ABBR]: createFormatter(fmtLegendNumber),

  // Dollars → all use compact notation
  [FORMAT.DOLLARS]: createFormatter(fmtLegendDollars),
  [FORMAT.DOLLARS_HUNDREDTHS]: createFormatter(fmtLegendDollars),
  [FORMAT.DOLLARS_ABBR]: createFormatter(fmtLegendDollars),

  // Percents → all round to integer
  [FORMAT.PERCENT]: createFormatter(fmtLegendPercent),
  [FORMAT.PERCENT_TENTHS]: createFormatter(fmtLegendPercent),
  [FORMAT.PERCENT_HUNDREDTHS]: createFormatter(fmtLegendPercent),
  [FORMAT.PERCENT_NUM]: createFormatter(fmtLegendPercentNum, 100),
};

/**
 * Format a value using a named format type.
 * @param {string | number} val
 * @param {Format} fmt
 * @param {string} [naLabel="N/A"]
 * @returns {string}
 */
export function formatNumber(val, fmt, naLabel = "N/A") {
  if (fmt in FORMATTERS) {
    return FORMATTERS[fmt](val, naLabel);
  }
  if (typeof val !== "string") {
    return val.toString();
  }
  return val;
}
